import { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, StickyPriceBar, DeviceImages, ConfirmModal } from 'mobily-ui-library/lib';

import { GlobalContext } from '../../../context/GlobalContext';
import { graphqlApi, createShoppingcart } from '../../../api/api';

import ErrorModal from '../../ErrorModal/ErrorModal';
import ProductVariants from '../ProductVariants/ProductVariants';
import ProductFinance from '../ProductFinance/ProductFinance';

import setCookie from '../../../Utils/setCookies';
import checkCookie from '../../../Utils/checkCookies';
import { useScroll } from '../../../Utils/useScroll';

import { PROD } from '../../../Constants';

import InfoMark from '../../../assets/InfoMark.svg';
import TamarabadgeEN from "../../../assets/TamarabadgeEN.svg";
import TamarabadgeAR from "../../../assets/TamarabadgeAR.jpg";
import CircleArrowRight from '../../../assets/CircleArrowRight.svg';

import './ProductDetails.scss';
import AppleCare from '../AppleCare/AppleCare';

const win: any = window;
const serverURL = win['base_portal_url'];
const errorPage = win['errorPageURL'];
const cartPage = win['cart_app_url'];
win.dataLayer = win.dataLayer || [];

interface Props {
    hits: any;
}

function ProductDetails({ hits }: Props) {
    const modalRef: any = useRef();
    const { scrollY } = useScroll();

    const { t } = useTranslation();
    const { state, dispatch }: any = useContext(GlobalContext);
    const { rtl, xCoId, seriesId, deviceTradeLink, lang } = state;

    const [soldOut, setSoldOut] = useState(false);
    const [cartFull, setCartFull] = useState(false);
    const [preOrder, setPreOrder] = useState(false);
    const [eligibleTamara, setEligibleTamara] = useState(false);
    const [stockStatus, setStockStatus] = useState(false);
    const [missingVariant, setMissingVariant] = useState(false);
    const [isTooltipVisible, setTooltipVisibility] = useState(false);
    const [modalVisable, setModalVisable] = useState<any>(false);
    const [imgZoomIn, setImgZoomIn] = useState<any>(false);
    const [initialPrice, setInitialPrice] = useState<any>(true);
    const [throttleFlag, setThrottleFlag] = useState<any>(false);
    const [subsidyFlow, setSubsidyFlow] = useState<string>('');
    const [availabilityMsg, setAvailabilityMsg] = useState<string>('');
    const [availabilityFlag, setAvailabilityFlag] = useState<any>(false);

    const [preOrderDate, setPreOrderDate] = useState('');
    const [price, setPrice] = useState('');
    const [productID, setProductID] = useState('');

    const [filters, setFilters] = useState<any>({});
    const [attributes, setAttributes] = useState<any>([]);
    const [isVariantError, setIsVariantError] = useState<any>(false);
    const [productVariationData, setProductVariationData] = useState<any>([]);
    const [enabledAttributeOptions, setEnabledAttributeOptions] = useState({});

    const [productVariants, setProductVariants] = useState<any>([]);
    const [productCategory, setProductCategory] = useState<any>({});
    const [deviceImages, setDeviceImages] = useState<any>([]);
    const [matchedProduct, setMatchedProduct] = useState<any>({});
    const [poid, setPoid] = useState('');

    useEffect(() => {
        if (hits.length > 0) {
            // const defaultVariantDetails = hits.filter((item: any) => item.isDefault)
            // const defaultVariant = defaultVariantDetails.length ? defaultVariantDetails[0] : hits[0]
            const defaultVariant = hits[0];
            const productCategory = defaultVariant.category;
            const variantAttributes = productCategory?.variantAttributes;
            setPoid(productCategory?.name?.valueEn); // app dynamic
            const attributes = Object.keys(productCategory)
                .filter((key) => variantAttributes.includes(key))
                .map((el, i) => {
                    if (el.toLowerCase().includes('color')) {
                        return {
                            id: i + 1,
                            name: el,
                            options: productCategory[el].map((item: any) => rtl ? item.valueAr : item.valueEn),
                            hex_options: productCategory[el].map((item: any) => item.code)
                        };
                    } else {
                        return {
                            id: i + 1,
                            name: el,
                            options: productCategory[el].map((item: any) => rtl ? item.valueAr : item.valueEn)
                        };
                    }
                }).filter((item: any) => item.options.length > 0 && item.options.some((op: any) => op != null))

            const productVariationStock = hits.filter((va: any) => va.stockStatus.code === 'In Stock');
            const productVariationData = productVariationStock.map((item: any) => {
                const newAttr = Object.keys(item)
                    .filter((key) => variantAttributes.includes(key))
                    .map((el, i) => {
                        if (el.toLowerCase().includes('color')) {
                            return {
                                id: i + 1,
                                name: el,
                                option: rtl ? item[el].valueAr : item[el].valueEn,
                                hex_option: item[el]?.code
                            };
                        } else {
                            return {
                                id: i + 1,
                                name: el,
                                option: rtl ? item[el].valueAr : item[el].valueEn
                            };
                        }
                    });

                return { ...item, attributes: newAttr };
            });

            setAttributes(attributes);
            setProductVariationData(productVariationData);

            let enabledAttributes: any = [];
            let newFilters: any = {};
            attributes.map((attribute) => {
                enabledAttributes[attribute.name] = attribute.options;
                newFilters[attribute.name] = "";
                return null;
            });

            setFilters(newFilters);
            setEnabledAttributeOptions(enabledAttributes);

            setProductVariants(hits);
            setProductCategory(productCategory);

            const stock = hits.some((hit: any) => hit.stockStatus.code === "In Stock");
            setSoldOut(!stock);

            const preOrder = defaultVariant.preOrder;
            setPreOrder(preOrder);
            setEligibleTamara(defaultVariant.eligibleForTamara)

            let preOrderDate = defaultVariant.preOrderDeliveryDate;
            const [yyyy, mm, dd] = preOrderDate.split(/-/g);
            preOrderDate = `${dd}/${mm}/${yyyy}`;
            setPreOrderDate(preOrderDate)

            const images = defaultVariant?.productURLs.map((item: any, index: number) => {
                return {
                    "id": index,
                    "smallImage": `${serverURL}${item.trim()}`,
                    "alt": "DeviceImages",
                    "isRefined": index === 0 ? true : false,
                    "bigImage": `${serverURL}${item.trim()}`
                }
            });

            setDeviceImages(images);
            gtmDataLayer(productCategory);
        }
    }, [hits]);

    useEffect(() => {
        onFindVariation();
    }, [filters])

    const gtmDataLayer = (category: any) => {
        const item_cat = category.deviceSubType.valueEn === "Smart Watches" ? "Wearables" : category.deviceSubType.valueEn === 'Smart Phones' ? 'Phones' : category.deviceSubType.valueEn;

        win.dataLayer.push({
            "event": "view_item",
            "ecommerce": {
                "user_id": "",
                "login_type": "not_logged_in",
                "screen_type": "device-page",
                "screen_name": "product_info",
                "language_selected": rtl ? "AR" : "EN",
                "currency": "SAR",
                "items": [{
                    "item_name": category?.name?.valueEn,
                    "item_category": item_cat,
                    "price": category.minPrice,
                    "item_id": seriesId,
                    "item_variants": "",
                    "index": 0,
                    "item_brand": category?.brand?.valueEn,
                    "currency": "SAR",
                    "item_list_name": "Phones and Accessories",
                    "quantity": 1,
                    "Order_type": preOrder ? "Pre-order" : "Order"
                }],
            }
        });
    }

    const onFindVariation = () => {
        let filterCount = getChoosenAttributesCount();
        if (filterCount === attributes.length) {
            onUpdateAttributes();
            var matching_variations = findMatchingVariations(productVariationData, filters);
            if (matching_variations.length > 0) {
                const product = matching_variations[0];
                const [yyyy, mm, dd] = product?.preOrderDeliveryDate.split(/-/g);
                const preOrderDate = `${dd}/${mm}/${yyyy}`;

                setMatchedProduct(product);
                setPreOrderDate(preOrderDate);
                setPreOrder(product?.preOrder)
                setEligibleTamara(product?.eligibleForTamara)
                setPrice(product?.price);
                setProductID(product?.id);
                setIsVariantError(false);
                setInitialPrice(false);
                dispatch({ type: 'PRODUCT_DATA', payload: { productData: product } })
                const jnCV = product?.preOrder ? 'Devices_Pre-Order' : 'Devices_StandAlone';
                const poidCV = product?.name?.valueEn;

                setPoid(poidCV);
                setCookie('device-journeyName', jnCV);
                setCookie('device-poid', poidCV);
            }
        } else {
            onUpdateAttributes();
            setPrice(productCategory?.minPrice);
            setInitialPrice(true);
            setPreOrder(false);
            setPreOrderDate('');
            setMatchedProduct({});
            dispatch({ type: 'PRODUCT_DATA', payload: { productData: {} } })
            setCookie('device-journeyName', 'Devices_StandAlone');
            setCookie('device-poid', '');
        }
    };

    const getChoosenAttributesCount = () => {
        let filterCount = 0;
        if (filters) {
            for (let key in filters) {
                if (filters[key] !== "") filterCount++;
            }
        }
        return filterCount;
    };

    const onUpdateAttributes = () => {
        let allAttributes = Object.assign({}, attributes);
        var enabledAttributes: any = [];

        attributes.forEach((attribute: any, index: any) => {
            let currentAttributes = Object.assign({}, filters);
            let current_attr_name = attribute.name;
            let new_attr_select = [];

            for (const key in allAttributes) {
                if (allAttributes[key].name === current_attr_name) {
                    new_attr_select[current_attr_name] = allAttributes[key].options.map(
                        (str: any, index: any) => ({ value: str, id: index })
                    );
                    break;
                }
            }

            var checkAttributes = currentAttributes;
            checkAttributes[current_attr_name] = "";

            var variations = findMatchingVariations(productVariationData, checkAttributes);

            for (var num in variations) {
                var variationAttributes = variations[num].attributes;

                for (var attr in variationAttributes) {
                    var attr_val = variationAttributes[attr].option;
                    var attr_name = variationAttributes[attr].name;

                    if (attr_name === current_attr_name) {
                        if (attr_val) {
                            var option_elements = new_attr_select[current_attr_name];
                            if (option_elements.length) {
                                for (let i = 0, len = option_elements.length; i < len; i++) {
                                    var option_element = option_elements[i];
                                    var option_value = option_element.value;

                                    if (attr_val === option_value) {
                                        new_attr_select[current_attr_name][i].state = "attached";
                                        break;
                                    }
                                }
                            }
                        } else {
                            let option_elements = new_attr_select[current_attr_name];
                            if (option_elements.length) {
                                for (let i = 0, len = option_elements.length; i < len; i++) {
                                    new_attr_select[current_attr_name][i].state = "attached";
                                }
                            }
                        }
                    }
                }
            }

            enabledAttributes[current_attr_name] = new_attr_select[
                current_attr_name
            ].map((na: any) => {
                if (na.state && na.state.includes("attached")) {
                    return na.value;
                }
            });
        });

        setEnabledAttributeOptions(enabledAttributes);
    };

    const findMatchingVariations = (variations: any, attributes: any) => {
        var matching = [];
        for (var i = 0; i < variations.length; i++) {
            var variation = variations[i];

            if (isMatch(variation.attributes, attributes)) {
                matching.push(variation);
            }
        }
        return matching;
    };

    const isMatch = (variation_attributes: any, attributes: any) => {
        var match = true;
        for (var attr_name in variation_attributes) {
            var attribute = variation_attributes[attr_name];
            var val1 = attribute.option;
            var val2 = attributes[attribute.name];
            if (
                val1 !== undefined &&
                val2 !== undefined &&
                val1.length !== 0 &&
                val2.length !== 0 &&
                val1 !== val2
            ) {
                match = false;
            }
        }
        return match;
    };

    const getVariantHandler = (key: any, value: any) => {
        let newFilters = filters;
        if (key.toLowerCase().includes('color')) {
            updateProductImages(key, value);
        }

        if (newFilters[key] === value) {
            newFilters[key] = "";
        } else {
            updateProductDate(key, value);
            newFilters[key] = value;
        }

        setFilters(() => {
            return { ...newFilters };
        });
    };

    const onSelectImg = (data: any) => {
    }

    let tooltipTimeout: any;

    const showTooltip = () => {
        tooltipTimeout = setTimeout(() => {
            const tooltipValue = modalRef.current.innerText;

            if (tooltipValue.length > 44) {
                setTooltipVisibility(true);
            } else {
                setTooltipVisibility(false);
            }
        }, 500);
    };

    const hideTooltip = () => {
        clearTimeout(tooltipTimeout);
        setTooltipVisibility(false);
    };

    const callGraphqlApi = (productId: any, flow: string) => {
        dispatch({ type: 'LOADER', payload: { loading: true } });
        dispatch({ type: 'PRODUCT_VARIANT_ID', payload: { productId } });

        graphqlApi(productId)
            .then((res: any) => {
                if (res.status === 200 && res?.data?.data.getProductOfferByID) {
                    const responseData = res?.data?.data?.getProductOfferByID[0];
                    dispatch({ type: 'GRAPHQL_RESPONSE_SUCCESS', payload: { graphQlResponse: res?.data?.data } });
                    const category = responseData.category.filter((type: any) => {
                        if (type.name == 'Devices and Accessories') return type
                    })[0];

                    if (responseData.stock.warehouseStatus !== 'Out of Stock') {
                        setStockStatus(false);
                        const sc_payload = {
                            productCharacteristic: res?.data?.data?.getProductOfferByID[0].specCharValueUse,
                            productId: productId,
                            category: {
                                id: category.id,
                                name: category.name
                            }
                        }
                        callCreateShoppingApi(sc_payload, flow);
                    } else {
                        dispatch({ type: 'LOADER', payload: { loading: false } })
                        setStockStatus(true);
                    }
                } else {
                    window.location.href = serverURL + errorPage + `?errorSource= ${t("ecommerce")}`;
                }
            }).catch((err: any) => {
                window.location.href = serverURL + errorPage + `?errorSource= ${t("ecommerce")}`;
            })
    }

    const callCreateShoppingApi = (sc_payload: any, flow: string) => {
        createShoppingcart(sc_payload, preOrder)
            .then((res: any) => {
                if (res.status === 201 && res.data) {
                    setCookie('device-scId', res.data.id, 60);
                    setCookie('device-xCoId', xCoId, 60);
                    setCookie('device-count', 1, 60);
                    dispatch({ type: 'SET_SHOPPING_CARTID', payload: { cartId: res.data.id } });
                    dispatch({ type: 'SET_SHOPPING_DATA', payload: { scData: res.data } });

                    if (flow === 'subsidy' || flow === 'instalment') {
                        dispatch({ type: 'LOADER', payload: { loading: false } });
                        dispatch({ type: 'PAGE_TO_DISPLAY', payload: { pageToDisplay: 'Subsidy' } });
                        dispatch({ type: 'DEVICE_FLOW', payload: { deviceFlow: flow } });
                    } else {
                        window.location.href = `${serverURL}${cartPage}`;
                    }

                } else if (res.status === 429) {
                    dispatch({ type: 'LOADER', payload: { loading: false } });
                    setThrottleFlag(true)
                } else if (res?.status === 200 && res?.data?.code === '404') {
                    const enMessage = res?.data?.message ? res?.data?.message : "";
                    const arMessage = res?.data?.messageAr ? res?.data?.messageAr : "";
                    const errMessage = rtl ? arMessage : enMessage;

                    setAvailabilityMsg(errMessage);
                    dispatch({ type: 'LOADER', payload: { loading: false } });
                    setAvailabilityFlag(true)
                } else {
                    window.location.href = serverURL + errorPage + `?errorSource= ${t("ecommerce")}`;
                }
            }).catch(err => {
                window.location.href = serverURL + errorPage + `?errorSource= ${t("ecommerce")}`;
            })
    }

    const handleAddCart = (flow: string) => {
        setSubsidyFlow(flow);

        const pId = new URL(window.location.href).searchParams.get('vId');
        const dataArray = Object.entries(filters).map(([name, value]) => ({ name, value }));

        if (pId) {
            callingShoppingCart(pId, flow)
        } else {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
            const newArray = [];
            for (const key in filters) {
                if (filters.hasOwnProperty(key)) {
                    if (filters[key] !== "") {
                        newArray.push(filters[key]);
                    }
                }
            }
            if (dataArray.length > 0 && (dataArray.length === newArray.length)) {
                callingShoppingCart(productID, flow)
            } else {
                setMissingVariant(true);
                setIsVariantError(true)
            }
        }
    }

    const callingShoppingCart = (pr_id: any, flow: string) => {
        const shoppingCartID = checkCookie('device-scId');
        const item_cat = productCategory?.deviceSubType.valueEn === "Smart Watches" ? "Wearables" : productCategory?.deviceSubType.valueEn === 'Smart Phones' ? 'Phones' : productCategory?.deviceSubType.valueEn;

        const gtmObj = {
            "event": "add_to_cart",
            "ecommerce": {
                "user_id": "",
                "login_type": "not_logged_in",
                "screen_type": "device-page",
                "screen_name": "product_info",
                "language_selected": rtl ? "AR" : "EN",
                "currency": "SAR",
                "value": "",
                "tax": "15% vat",
                "items": [{
                    "item_name": productCategory?.name?.valueEn,
                    "item_category": item_cat,
                    "price": matchedProduct?.price,
                    "apple_care": "no",
                    "item_id": seriesId,
                    "item_variants": matchedProduct?.id,
                    "index": 0,
                    "item_brand": productCategory?.brand?.valueEn,
                    "currency": "SAR",
                    "item_list_name": "Phones and Accessories",
                    "quantity": 1,
                    "discount": "",
                    "discount_type": "",
                    "Order_type": preOrder ? "Pre-order" : "Order"
                }],
            }
        }

        const foundIndex = win.dataLayer.findIndex((x: any) => x.event == gtmObj.event);
        if (foundIndex >= 0) {
            win.dataLayer[foundIndex] = gtmObj;
        } else {
            win.dataLayer.push(gtmObj);
        }

        if (shoppingCartID) {
            window.scroll({ top: 0, left: 0, behavior: "smooth" });
            setCartFull(true);
        } else {
            setCartFull(false);
            callGraphqlApi(pr_id, flow);
        }
    }

    const handleModalReplace = () => {
        const pId = new URL(window.location.href).searchParams.get('vId');
        setCartFull(false);
        if (pId) {
            callGraphqlApi(pId, subsidyFlow);
        } else {
            callGraphqlApi(productID, subsidyFlow);
        }
    }

    const handleModalCancel = () => {
        setCartFull(false);
        document.body.style.overflow = 'auto'
    }

    const handleStockStatus = () => {
        setStockStatus(false);
        document.body.style.overflow = 'auto';
    }

    const handleMissingVariant = () => {
        setMissingVariant(false);
        document.body.style.overflow = 'auto';
    }

    const updateProductImages = (key: string, info: any) => {
        const product = productVariants.filter((pr: any) => {
            if (rtl) {
                return pr[key].valueAr === info
            } else {
                return pr[key].valueEn === info
            }
        });
        const deviceImg = product[0].productURLs.map((item: any, index: number) => {
            return ({
                "id": index,
                "smallImage": `${serverURL}${item.trim()}`,
                "alt": "DeviceImages",
                "isRefined": index === 0 ? true : false,
                "bigImage": `${serverURL}${item.trim()}`
            })
        });
        setDeviceImages(deviceImg);
    }

    const updateProductDate = (attr: string, info: any) => {
        const res = productVariants.filter((item: any) => {
            if (rtl) {
                return item[attr].valueAr === info
            } else {
                return item[attr].valueEn === info
            }
        })[0];

        const [yy, mm, dd] = res?.preOrderDeliveryDate.split(/-/g);
        const preOrderDate = `${dd}-${mm}`;
        setPreOrderDate(preOrderDate);
    }

    const getImgModal = () => {
        setModalVisable(true)
    }

    const redirectToTradeIn = () => {
        window.open(deviceTradeLink, '_blank');
    }

    const onClickZoom = () => {
        setImgZoomIn(!imgZoomIn)
    }

    const handleThrottling = () => {
        setThrottleFlag(false)
    }

    const handleAvailability = () => {
        setAvailabilityFlag(false)
    }

    const prodTamaraURL = `https://cdn.tamara.co/widget-v2/tamara-widget.html?lang=${lang}&public_key=006d10cc-9566-459a-be08-042ed9599f4a&country=SA&amount=${price.length === 0 ? productCategory?.minPrice : price}&inline_type=2`;
    const lowerTamaraURL = `https://cdn-sandbox.tamara.co/widget-v2/tamara-widget.html?lang=${lang}&public_key=c7b0077d-f0b2-4bef-b376-af54456eda37&country=SA&amount=${price.length === 0 ? productCategory?.minPrice : price}&inline_type=2`;

    return (
        <>
            <div id="ecommerceJourneyName" style={{ display: "none" }}>{preOrder ? 'Devices_Pre-Order' : 'Devices_StandAlone'}</div>
            <div id="xCorrelationId" style={{ display: "none" }}>{xCoId}</div>
            <div id="sectionId" style={{ display: "none" }}>ProductDetails</div>
            <div id="DeviceName" style={{ display: "none" }}>{poid}</div>

            <div className={rtl ? "deviceDetails__container container deviceDetails__Right" : "deviceDetails__container container"}>
                <ConfirmModal id='modal-devices' showModal={modalVisable} onClickClose={() => { setModalVisable(false) }}>
                    <div className={`popup_device_images ${imgZoomIn === true ? 'zoomIn_img' : 'zoomOut_img'}`}>
                        <DeviceImages
                            id='devices-images'
                            onClick={onSelectImg}
                            VisibleArrowLeft={false}
                            VisibleArrowRight={true}
                            bigImage={serverURL + productCategory?.defaultURL}
                            data={deviceImages}
                            onClickimgModal={onClickZoom}
                        />
                    </div>
                </ConfirmModal>
                {productVariants.length > 0 && (
                    <>
                        {scrollY > 100 ? (
                            <div className="DD__TitleScroll">
                                <Typography id='brand-name' rtl={rtl} className='scrollTitle devices-container'>{rtl ? `${productCategory?.brand?.valueAr} ${productCategory?.name?.valueAr}` : `${productCategory?.brand?.valueEn} ${productCategory?.name?.valueEn}`}</Typography>
                            </div>
                        ) : (
                            <div className="DD__Title">
                                {preOrder && (
                                    <div className='preOrder_container'>
                                        <Typography id='pre-order' rtl={rtl} className='preOrder_text'>{t("pre_order")}</Typography>
                                    </div>
                                )}

                                <Typography id='brand-name' rtl={rtl} className='brandNameNew'>{rtl ? productCategory?.brand?.valueAr : productCategory?.brand?.valueEn}</Typography>
                                <div className="tooltip-container" onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
                                    <div id='brand-name' ref={modalRef} className="modalName">
                                        {rtl ? productCategory?.name?.valueAr : productCategory?.name?.valueEn}
                                    </div>
                                    {isTooltipVisible && (
                                        <Typography id='brand-types' rtl={rtl} className="custom-tooltip">
                                            {rtl ? productCategory?.name?.valueAr : productCategory?.name?.valueEn}
                                        </Typography>
                                    )}
                                </div>
                                <Typography id='starting-prices' rtl={rtl} className='modalPrice'>{t("starting_from")} {productCategory?.minPriceWithoutVAT} {t('SAR')}</Typography>
                                {preOrder && (
                                    <Typography id='pre-order' rtl={rtl} className='preOrder_text'>
                                        {`${t("For_pre_order")} ${preOrderDate}`}
                                    </Typography>
                                )}
                                {soldOut && (
                                    <Typography id='out-of-stock' rtl={rtl} className='soldOut_text'>
                                        {t('Item_out_of_Stock')}
                                    </Typography>
                                )}
                            </div>
                        )}

                        <div id='devices-details' className="deviceDetails__subcontainer">
                            <div className="half">
                                <div id='devices-images' className={scrollY > 100 ? 'productImgContainer productImg__Scroll' : 'productImgContainer'}>
                                    <DeviceImages
                                        onClick={onSelectImg}
                                        VisibleArrowLeft={false}
                                        VisibleArrowRight={true}
                                        bigImage={serverURL + productCategory?.defaultURL}
                                        data={deviceImages}
                                        onClickimgModal={getImgModal}
                                    />
                                </div>
                            </div>
                            <div className="gap" />
                            <div className="half">
                                <div id='device-variants' className={scrollY > 100 ? 'deviceVariants__container DVC__Scroll' : 'deviceVariants__container'}>
                                    {eligibleTamara && (
                                        <a href={win["env_variable"]?.toUpperCase() === PROD ? prodTamaraURL : lowerTamaraURL} target='_blank'>
                                            <div id='tamara-payment-card' className={`tamara_payment_card ${rtl ? 'pl-3' : 'pr-3'}`}>
                                                <div className='card_content'>
                                                    <div className='card_text_section'>
                                                        <img className="Tamara__Image" src={rtl ? TamarabadgeAR : TamarabadgeEN} alt="TamarabadgeEN" width={62} height={24} />
                                                        <div className='desktop-text'>
                                                            <Typography rtl={rtl} className='device_tradeIn_title'>{t('split_cost_smaller_payment')}</Typography>
                                                            <Typography rtl={rtl} className='device_tradeIn_subTitle' >{t('no_interest_no_hidden')}</Typography>
                                                        </div>
                                                        <div className='mobile-text'>
                                                            <Typography rtl={rtl} className='device_tradeIn_title'>{t('split_cost_smaller_payment_mobile')}</Typography>
                                                            <Typography rtl={rtl} className='device_tradeIn_subTitle' >{t('no_interest_no_hidden_mobile')}</Typography>
                                                        </div>
                                                    </div>
                                                    <div className='device_tradeIn_new_container'>
                                                        <Typography className='device_tradeIn_new'>{t('new')}</Typography>
                                                    </div>
                                                </div>
                                                <div className='tamara_seperator'></div>
                                            </div>
                                        </a>
                                    )}

                                    <div id='device-tradeIn' className='device_tradeIn_container' >
                                        <div className='device_tradeIn_title_container'>
                                            <Typography className='device_tradeIn_title'>{t('device_trade_in')} <img src={InfoMark} alt='image' /> </Typography>
                                            <div className='device_tradeIn_new_container'>
                                                <Typography className='device_tradeIn_new'>{t('new')}</Typography>
                                            </div>
                                        </div>
                                        <Typography rtl={rtl} className='device_tradeIn_subTitle' >{t('trade_existing_phone')}</Typography>
                                        <div className='device_tradeIn_link' onClick={redirectToTradeIn}>
                                            <Typography rtl={rtl}>{t('get_started')} </Typography>
                                            <div className='circle_arrow_container'>
                                                <img className={`circle_arrow circle_arrow_${rtl ? 'right' : ''}`} src={CircleArrowRight} alt='image' />
                                            </div>
                                        </div>
                                    </div>

                                    {attributes.map((attribute: any) => {
                                        return (
                                            <ProductVariants
                                                key={attribute.id}
                                                selectedButtons={filters}
                                                attributeId={attribute.id}
                                                isVariantError={isVariantError}
                                                attributeName={attribute.name}
                                                getVariantHandler={getVariantHandler}
                                                attributeOptions={attribute?.options?.filter((item: any) => item)}
                                                optionColors={attribute.hex_options}
                                                enabledAttributeOptions={enabledAttributeOptions}
                                            />
                                        )
                                    })}
                                </div>
                                {(preOrder && matchedProduct?.category?.deviceSubType?.valueEn.toLowerCase() !== 'accessories') && (
                                    <AppleCare rtl={rtl} />
                                )}
                                {(matchedProduct?.eligibleSubsidy || matchedProduct?.eligibleForInstallment) && (
                                    <ProductFinance
                                        rtl={rtl}
                                        handleAddCart={handleAddCart}
                                        subsidyFlag={matchedProduct?.eligibleSubsidy}
                                        instalmentFlag={matchedProduct?.eligibleForInstallment}
                                    />
                                )}
                            </div>
                        </div>

                        <StickyPriceBar
                            rtl={rtl}
                            id='sticky-price'
                            priceId='total-price'
                            buttonId='add-to-cart'
                            priceTitle={initialPrice ? t('starting_from') : ''}
                            price={price.length === 0 ? productCategory?.minPrice : price}
                            currencyType={` ${t("SAR")}`}
                            vat={t("Incl_Vat")}
                            buttonValue={t('Add_to_cart')}
                            onClick={() => handleAddCart('cart')}
                        />
                    </>
                )}

                {cartFull && (
                    <ErrorModal
                        id='duplicate-shopping-cartId'
                        rtl={rtl}
                        title={t("Your_Cart_Full")}
                        description={t('One_Item_At_Moment')}
                        firstButtonText={t('Replace_Cart')}
                        secButtonText={t('Cancel')}
                        modalFirstButtonClick={handleModalReplace}
                        modalSecondButtonClick={handleModalCancel}
                    />
                )}

                {stockStatus && (
                    <ErrorModal
                        id='error-out-of-stock'
                        rtl={rtl}
                        title={t('Item_Sold_Out')}
                        description={t('Combination_Out_of_Stock')}
                        firstButtonText={t('GOT_IT')}
                        modalFirstButtonClick={handleStockStatus}
                    />
                )}

                {throttleFlag && (
                    <ErrorModal
                        id='error-throttle'
                        rtl={rtl}
                        title={t('High_Order_Demand')}
                        description={t('')}
                        firstButtonText={t('GOT_IT')}
                        modalFirstButtonClick={handleThrottling}
                    />
                )}

                {availabilityFlag && (
                    <ErrorModal
                        id='error-availability'
                        rtl={rtl}
                        title={availabilityMsg}
                        description={t('')}
                        firstButtonText={t('GOT_IT')}
                        modalFirstButtonClick={handleAvailability}
                    />
                )}

                {missingVariant && (
                    <ErrorModal
                        id='error-select-variant'
                        rtl={rtl}
                        title={t('select_all_specs')}
                        description={t('')}
                        firstButtonText={t('GOT_IT')}
                        modalFirstButtonClick={handleMissingVariant}
                    />
                )}
            </div>
        </>
    )
}

export default ProductDetails;